import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, 
  CheckCircle, 
  X, 
  Eye, 
  AlertTriangle, 
  Clock,
  Search,
  Filter,
  MoreHorizontal,
  ExternalLink,
  Flag,
  Ban,
  MessageSquare
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

interface ModerationItem {
  id: string;
  platform: string;
  author: string;
  content: string;
  type: 'post' | 'comment' | 'mention';
  flagReason: string;
  severity: 'low' | 'medium' | 'high';
  status: 'pending' | 'approved' | 'rejected' | 'flagged';
  timestamp: string;
  engagement: {
    likes: number;
    shares: number;
    comments: number;
  };
  aiConfidence: number;
}

export function ContentModeration() {
  const [moderationQueue, setModerationQueue] = useState<ModerationItem[]>([
    {
      id: '1',
      platform: 'twitter',
      author: '@suspicious_user',
      content: 'Check out this amazing deal! Click here to win $1000 instantly! Limited time offer, dont miss out!',
      type: 'post',
      flagReason: 'Spam keywords detected',
      severity: 'high',
      status: 'pending',
      timestamp: '2 minutes ago',
      engagement: { likes: 2, shares: 1, comments: 0 },
      aiConfidence: 94
    },
    {
      id: '2',
      platform: 'instagram',
      author: '@random_commenter',
      content: 'Your product is terrible, worst experience ever. Complete waste of money and time.',
      type: 'comment',
      flagReason: 'Negative sentiment',
      severity: 'medium',
      status: 'pending',
      timestamp: '15 minutes ago',
      engagement: { likes: 0, shares: 0, comments: 2 },
      aiConfidence: 78
    },
    {
      id: '3',
      platform: 'twitter',
      author: '@brand_mention',
      content: 'Just tried @yourbrand for the first time and I have to say, the experience could be improved.',
      type: 'mention',
      flagReason: 'Mixed sentiment',
      severity: 'low',
      status: 'pending',
      timestamp: '1 hour ago',
      engagement: { likes: 5, shares: 2, comments: 3 },
      aiConfidence: 65
    },
    {
      id: '4',
      platform: 'facebook',
      author: 'John Fake Profile',
      content: 'Follow me for more cryptocurrency investment tips! Get rich quick with this secret method!',
      type: 'post',
      flagReason: 'Cryptocurrency spam',
      severity: 'high',
      status: 'flagged',
      timestamp: '2 hours ago',
      engagement: { likes: 0, shares: 0, comments: 1 },
      aiConfidence: 91
    }
  ]);

  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const handleModerate = (id: string, action: 'approve' | 'reject' | 'flag') => {
    setModerationQueue(items => 
      items.map(item => 
        item.id === id ? { ...item, status: action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'flagged' } : item
      )
    );
    
    const actionText = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'flagged';
    toast.success(`Content ${actionText} successfully`);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'low': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'flagged': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'twitter': return '𝕏';
      case 'instagram': return '📷';
      case 'facebook': return '👥';
      case 'linkedin': return '💼';
      default: return '📱';
    }
  };

  const filteredItems = moderationQueue.filter(item => {
    const matchesFilter = selectedFilter === 'all' || item.status === selectedFilter;
    const matchesSearch = searchQuery === '' || 
      item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const pendingCount = moderationQueue.filter(item => item.status === 'pending').length;
  const flaggedCount = moderationQueue.filter(item => item.status === 'flagged').length;
  const todayProcessed = 45; // This would come from your analytics
  const accuracyRate = 96.2; // This would come from your analytics

  const ModerationActions = ({ item }: { item: ModerationItem }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={() => handleModerate(item.id, 'approve')}
          className="text-green-600"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Approve
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleModerate(item.id, 'reject')}
          className="text-red-600"
        >
          <X className="h-4 w-4 mr-2" />
          Reject
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleModerate(item.id, 'flag')}
          className="text-purple-600"
        >
          <Flag className="h-4 w-4 mr-2" />
          Flag for Review
        </DropdownMenuItem>
        <DropdownMenuItem>
          <ExternalLink className="h-4 w-4 mr-2" />
          View Original
        </DropdownMenuItem>
        <DropdownMenuItem className="text-red-600">
          <Ban className="h-4 w-4 mr-2" />
          Block User
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Moderation</h1>
          <p className="text-gray-600 mt-1">
            Review and moderate flagged content across all your social platforms
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input 
              placeholder="Search content..." 
              className="pl-10 w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={selectedFilter} onValueChange={setSelectedFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Flagged Content</p>
                <p className="text-2xl font-bold text-gray-900">{flaggedCount}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Processed Today</p>
                <p className="text-2xl font-bold text-gray-900">{todayProcessed}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">AI Accuracy</p>
                <p className="text-2xl font-bold text-gray-900">{accuracyRate}%</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Moderation Queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Moderation Queue</span>
            {pendingCount > 0 && (
              <Badge className="bg-orange-100 text-orange-800">
                {pendingCount} pending
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending" className="space-y-4">
            <TabsList>
              <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>
              <TabsTrigger value="flagged">Flagged ({flaggedCount})</TabsTrigger>
              <TabsTrigger value="all">All Items ({moderationQueue.length})</TabsTrigger>
              <TabsTrigger value="processed">Processed</TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-4">
              {filteredItems.filter(item => item.status === 'pending').length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Items</h3>
                  <p className="text-gray-600">
                    All content has been reviewed. Great job keeping your feeds clean!
                  </p>
                </div>
              ) : (
                filteredItems.filter(item => item.status === 'pending').map((item) => (
                  <div key={item.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">{getPlatformIcon(item.platform)}</div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{item.author}</span>
                            <Badge variant="outline" className="text-xs">
                              {item.type}
                            </Badge>
                            <Badge className={`text-xs ${getSeverityColor(item.severity)}`}>
                              {item.severity} risk
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{item.timestamp}</p>
                        </div>
                      </div>
                      <ModerationActions item={item} />
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-gray-800">{item.content}</p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>👍 {item.engagement.likes}</span>
                        <span>🔄 {item.engagement.shares}</span>
                        <span>💬 {item.engagement.comments}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          AI Confidence: {item.aiConfidence}%
                        </span>
                        <div className="flex items-center space-x-1">
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                          <span className="text-xs text-gray-600">{item.flagReason}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-3 pt-3 border-t">
                      <Button 
                        size="sm" 
                        onClick={() => handleModerate(item.id, 'approve')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleModerate(item.id, 'reject')}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleModerate(item.id, 'flag')}
                      >
                        <Flag className="h-4 w-4 mr-2" />
                        Flag
                      </Button>
                      <Button size="sm" variant="ghost">
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="flagged" className="space-y-4">
              {filteredItems.filter(item => item.status === 'flagged').map((item) => (
                <div key={item.id} className="border rounded-lg p-4 border-red-200 bg-red-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getPlatformIcon(item.platform)}</div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{item.author}</span>
                          <Badge variant="destructive">Flagged</Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{item.timestamp}</p>
                      </div>
                    </div>
                    <ModerationActions item={item} />
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 mb-3 border">
                    <p className="text-gray-800">{item.content}</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700 font-medium">{item.flagReason}</span>
                    </div>
                    <Badge className="bg-red-100 text-red-800">
                      Requires Review
                    </Badge>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="all" className="space-y-4">
              {filteredItems.map((item) => (
                <div key={item.id} className={`border rounded-lg p-4 ${
                  item.status === 'flagged' ? 'border-red-200 bg-red-50' :
                  item.status === 'approved' ? 'border-green-200 bg-green-50' :
                  item.status === 'rejected' ? 'border-gray-200 bg-gray-50' :
                  'hover:shadow-md transition-shadow'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getPlatformIcon(item.platform)}</div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{item.author}</span>
                          <Badge className={`text-xs ${getStatusColor(item.status)}`}>
                            {item.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{item.timestamp}</p>
                      </div>
                    </div>
                    {item.status === 'pending' && <ModerationActions item={item} />}
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="text-gray-800">{item.content || 'No content available'}</p>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span>👍 {item.engagement.likes}</span>
                      <span>🔄 {item.engagement.shares}</span>
                      <span>💬 {item.engagement.comments}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      <span>{item.flagReason}</span>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="processed" className="space-y-4">
              {filteredItems.filter(item => item.status !== 'pending').map((item) => (
                <div key={item.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getPlatformIcon(item.platform)}</div>
                      <div>
                        <span className="font-medium text-gray-900">{item.author}</span>
                        <Badge className={`ml-2 text-xs ${getStatusColor(item.status)}`}>
                          {item.status}
                        </Badge>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">{item.timestamp}</span>
                  </div>
                  <p className="text-gray-600 mt-2 line-clamp-2">{item.content}</p>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

export default ContentModeration;